import { Search } from 'lucide-react'
import { LuX } from 'react-icons/lu'
import TagPill from '#/components/common/TagPill'
import line from '#/assets/line.svg'
import type { Tag } from '#/features/courses/types/course.types'
import { cn } from '#/lib/utils'

type BlogsFilterProps = {
  tags: Tag[]
  activeTags: string[]
  onToggleTag: (slug: string) => void
  onClearTags: () => void
  searchQuery?: string
  onSearchChange?: (query: string) => void
  className?: string
  showSearch?: boolean
  variant?: 'sidebar' | 'drawer'
}

export default function BlogsFilter({
  tags,
  activeTags,
  onToggleTag,
  onClearTags,
  searchQuery = '',
  onSearchChange,
  className,
  showSearch = true,
  variant = 'sidebar',
}: BlogsFilterProps) {
  const isAllActive = activeTags.length === 0

  return (
    <div className={cn('flex w-full flex-col gap-6', className)}>
      {/* Search Bar */}
      {showSearch && (
        <div>
          <label className="flex items-center gap-3 rounded-[14px] border border-neutral-200 bg-white px-4 py-3 text-[14px] text-neutral-600 shadow-xs focus-within:border-secondary-500 focus-within:ring-2 focus-within:ring-secondary-500/20">
            <Search className="h-4 w-4 text-neutral-400 shrink-0" />
            <input
              className="w-full bg-transparent text-[15px] text-neutral-800 placeholder:text-neutral-400 focus:outline-none"
              placeholder="Search articles..."
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange?.('')}
                className="text-neutral-400 hover:text-neutral-600 p-0.5"
              >
                <LuX className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
        </div>
      )}

      {/* Tags Box */}
      <div
        className={cn(
          'rounded-[18px] bg-white p-6 shadow-xs border border-neutral-100',
          variant === 'drawer' && 'border-neutral-200/80 bg-white',
        )}
      >
        <div className="flex flex-col items-start gap-2.5">
          <h3 className="text-xl font-bold text-neutral-900">Tags</h3>
          <img src={line} alt="Line" className="h-4 w-auto" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2.5">
          <TagPill label="All" active={isAllActive} onClick={onClearTags} />
          {tags.map((tag) => (
            <TagPill
              key={tag.id || tag.slug}
              label={tag.name}
              active={activeTags.includes(tag.slug)}
              onClick={() => onToggleTag(tag.slug)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
